<template>
  <div>
    <InputLabel
      :label="label"
      :input-id="inputId"
      :required="required"
    />

    <div :class="label ? 'mt-2' : ''">
      <div
        ref="containerRef"
        class="-outline-offset-1 bg-surface-canvas dark:bg-surface-canvas/5 flex items-center outline-1 relative rounded-md"
        :class="wrapperOutline"
      >
        <USelectMenu
          v-model:search-term="searchQuery"
          :model-value="selectedCode"
          :items="countryItems"
          value-key="value"
          :disabled="isSingleCountryMode"
          :search-input="searchInputProps"
          :content="contentProps"
          variant="none"
          :trailing="false"
          :ui="selectUi"
          ignore-filter
          @update:model-value="onCountrySelect"
        >
          <span
            v-if="loading"
            class="font-medium text-content-level-400"
            >...</span
          >

          <span
            v-else-if="showFlagInButton && selectedCountry"
            class="flex font-medium gap-0.5 items-center"
          >
            <svg
              v-if="erroredFlags.has(selectedCountry.flag)"
              class="shrink-0 size-5 text-content-level-400"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M4 20V3" />

              <path d="M4 3h11l-1.5 3L15 9H4" />
            </svg>

            <img
              v-else
              :src="selectedCountry.flag"
              :alt="selectedCountry.code"
              :data-country-flag="selectedCountry.flag"
              class="object-cover rounded-sm shrink-0 size-5"
              @error="onCountryFlagError"
            />

            <span v-if="buttonDisplay !== 'flag'">{{ buttonLabel }}</span>
          </span>

          <span
            v-else
            class="font-medium"
            >{{ buttonLabel }}</span
          >

          <template #item-label="{ item }">
            <span
              class="flex gap-3 items-center truncate"
              :class="[isSelectedCountry(item.country) ? 'font-semibold' : 'font-normal']"
            >
              <img
                v-if="
                  variant === 'full' && item.country.flag && !erroredFlags.has(item.country.flag)
                "
                :src="item.country.flag"
                :alt="item.country.code"
                :data-country-flag="item.country.flag"
                class="object-cover rounded-sm shrink-0 size-5"
                @error="onCountryFlagError"
              />

              <svg
                v-else-if="variant === 'full'"
                class="shrink-0 size-5 text-content-level-400"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M4 20V3" />

                <path d="M4 3h11l-1.5 3L15 9H4" />
              </svg>

              <span>{{ item.label }}</span>
            </span>
          </template>

          <template #item-trailing="{ item }">
            <CheckIcon
              v-if="isSelectedCountry(item.country)"
              class="size-5 text-primary"
              aria-hidden="true"
            />
          </template>
        </USelectMenu>

        <div class="bg-surface-level-200 dark:bg-surface-canvas/10 h-6 w-px" />

        <input
          :id="inputId"
          ref="nativeInput"
          :value="phoneValue"
          type="tel"
          inputmode="tel"
          :name="name"
          :placeholder="placeholder"
          :readonly="readonly"
          :autocomplete="autocomplete"
          :aria-required="required ? 'true' : undefined"
          :aria-invalid="hasError || undefined"
          :aria-describedby="describedByIds"
          class="bg-surface-clear block border-0 dark:placeholder:text-content-level-500 dark:text-content-inverse flex-1 min-w-0 outline-none pl-1 placeholder:text-content-level-400 py-1.5 sm:text-sm/6 text-base text-content-level-900"
          @beforeinput="handleBeforeInput"
          @input="onPhoneInput"
          @change="reconcileNativeValue"
          @keydown.enter="reconcileNativeValue"
          @blur="onPhoneBlur"
        />
      </div>

      <p
        v-if="displayError"
        :class="fieldControlUi.formField.error"
      >
        {{ displayError }}
      </p>

      <div
        v-if="hasHelp"
        :id="helpId"
        :class="fieldControlUi.formField.help"
      >
        <slot name="help" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef, useId, useTemplateRef } from 'vue'
import USelectMenu from '@nuxt/ui/components/SelectMenu.vue'
import { CheckIcon } from '@heroicons/vue/20/solid'
import { usePhoneFieldSync } from '~/composables/usePhoneFieldSync'
import { usePhoneInput } from '~/composables/usePhoneInput'
import { useCountryPicker } from '~/composables/useCountryPicker'
import { PhoneNumber } from '~/utils/PhoneNumber'
import type { CountryCode } from '~/enums/CountryCode'
import { phoneConfig } from '~/config/phoneConfig'
import { fieldControlUi } from '~/design-system/base/fieldControlUi'
import InputLabel from './InputLabel.vue'

type Variant = 'full' | 'code' | 'prefix'
type ButtonDisplay = 'prefix' | 'code' | 'flag' | 'flagPrefix' | 'flagCode' | 'codePrefix'

const props = withDefaults(
  defineProps<{
    /** Controlled phone number value. */
    modelValue?: string
    /** Hint displayed when the phone field has no value. */
    placeholder?: string
    /** Label displayed above the phone field. */
    label?: string
    /** Whether the phone field is required and displays a required marker. */
    required?: boolean
    /** Whether the phone field can be viewed but not edited. */
    readonly?: boolean
    /** HTML form field name assigned to the phone input. */
    name?: string
    /** HTML identifier assigned to the phone input. */
    id?: string
    /** HTML autocomplete hint assigned to the phone input. */
    autocomplete?: string
    /** HTML identifier of the message that describes the phone input. */
    describedBy?: string
    /** External validation message displayed while the entry itself has no local error. */
    error?: string
    /** Country option format used by the picker list. */
    variant?: Variant
    /** Country information shown in the picker button. */
    buttonDisplay?: ButtonDisplay
  }>(),
  {
    autocomplete: 'tel',
    buttonDisplay: 'code',
    describedBy: '',
    error: '',
    id: '',
    label: '',
    modelValue: '',
    name: 'phone',
    placeholder: '(555) 123-4567',
    variant: 'full'
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  blurred: []
}>()

const slots = defineSlots<{
  /** Content rendered below the entry, linked via aria-describedby. */
  help?: () => unknown
}>()

const isSingleCountryMode = computed(() => phoneConfig.allowedCountries.length <= 1)

const required = computed(() => props.required)

const containerRef = useTemplateRef<HTMLElement>('containerRef')
const {
  buttonLabel,
  contentProps,
  countryItems,
  erroredFlags,
  isSelectedCountry,
  loading,
  onCountryFlagError,
  onCountrySelect,
  searchInputProps,
  searchQuery,
  selectUi,
  selectedCode,
  selectedCountry,
  showFlagInButton
} = useCountryPicker({
  buttonDisplay: toRef(props, 'buttonDisplay'),
  container: containerRef,
  variant: toRef(props, 'variant')
})

const selectedCountryCode = (): CountryCode =>
  phoneConfig.allowedCountries.find((code) => code === selectedCode.value) ??
  PhoneNumber.defaultCountry

const field = usePhoneInput({
  country: selectedCountryCode,
  onCountryDetected: onCountrySelect,
  required
})
const { cleanPhone, isValid, phoneError, phoneValue, handleBeforeInput } = field

const nativeInput = useTemplateRef<HTMLInputElement>('nativeInput')
const {
  onBlur: onPhoneBlur,
  onInput: onPhoneInput,
  reconcileNativeValue,
  validateReconciled
} = usePhoneFieldSync({
  emitBlur: () => {
    emit('blurred')
  },
  emitUpdate: (entry) => {
    emit('update:modelValue', entry)
  },
  field,
  input: nativeInput,
  modelValue: () => props.modelValue
})

// The field's own verdict is the most specific message; an external error shows when the entry is acceptable.
const displayError = computed(() =>
  phoneError.value === '' && props.error !== undefined ? props.error : phoneError.value
)
const hasError = computed(() => displayError.value !== undefined && displayError.value !== '')
const wrapperOutline = computed(() =>
  hasError.value
    ? fieldControlUi.control.outlineInvalid
    : [fieldControlUi.control.outline, fieldControlUi.control.focusWithin]
)

const generatedId = useId()
const inputId = computed(() => props.id || generatedId)
const hasHelp = computed(() => slots.help !== undefined)
const helpId = computed(() => `${inputId.value}-help`)
const describedByIds = computed(() => {
  const ids = [props.describedBy, hasHelp.value ? helpId.value : ''].filter((id) => id !== '')
  return ids.length > 0 ? ids.join(' ') : undefined
})

defineExpose({ cleanPhone, isValid, validate: validateReconciled })
</script>
